import React, { Component } from 'react';
import { Modal, ModalBody, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import InputField from './inputs/inputField';
import FileChooserPreview from './inputs/fileChooserPreview';
import FilesUploaderModal from './files/filesUploaderModal';
import ToggleField from './inputs/toggleField';
import SelectField from './inputs/selectField';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';


class BadgeEditor extends Component {
	constructor(props) {
		super(props);

		const defaultConfig = {
			badgeWidth: 332, // pixels
			badgeHeight: 529, // pixels
			fieldsConfig: [],
			enableQRCode: true,
			qrCode: { x: 15, y: 415, size: 100 },
			backgroundImage: 'https://assets-simpleevents.s3.amazonaws.com/betaorg/SE_66c8ca9f1913e99e9baebe93/assets/BADGETEMPLATE%20in%20MM.png',
			includeBackground: true
		};
		const initialConfig = this.props.initialConfig ? { ...defaultConfig, ...this.props.initialConfig } : defaultConfig;
		this.state = {
			baseFields: [
				{ value: 'firstName', label: 'First Name' },
				{ value: 'lastName', label: 'Last Name' },
				{ value: 'fullName', label: 'Full Name' },
				{ value: 'email', label: 'Email' },
				{ value: 'company', label: 'Company' }
			],
			config: JSON.parse(JSON.stringify(initialConfig)),
			baseConfiguration: JSON.parse(JSON.stringify(initialConfig)),
			scaleX: 400 / 1040,
			scaleY: 1654 / 1654,
			draggedElement: null,
			isResizing: false,
			resizeHandle: null,
			offsetX: 0,
			offsetY: 0,
			originalRect: null,
			originalConfig: null,
			dragUpdate: 0,
			editing: false,
			uploadingBackground: false,
			selectedBaseField: null,
			selectedTab: 'editor', // Default to editor tab
			libraryPanelOpen: false,
			stylePanelOpen: false,
			settingsPanelOpen: false,
			componentDetailsOpen: true, // Match pageEditor.js pattern
			componentDetailsExpanded: false, // Match pageEditor.js pattern
			focusedComponent: '', // Match pageEditor.js pattern
			componentSelected: false, // Match pageEditor.js pattern
			selectedPartialIndex: null // Match pageEditor.js pattern (renamed from selectedFieldIndex)
		};
		this.badgeContainerRef = React.createRef();
		this.fieldRefs = {};
		this.qrElementRef = React.createRef();
		this.backgroundImgRef = React.createRef();
	}

	componentDidMount() {
		this.updateDisplay();
		document.addEventListener('mousemove', this.handleMouseMove);
		document.addEventListener('mouseup', this.handleMouseUp);
	}

	componentWillUnmount() {
		document.removeEventListener('mousemove', this.handleMouseMove);
		document.removeEventListener('mouseup', this.handleMouseUp);
	}

	clamp = (value, min, max) => {
		return Math.max(min, Math.min(max, value));
	};

	updateDisplay = () => {
		const { config } = this.state;

		// Update background
		if (this.badgeContainerRef.current) {
			this.badgeContainerRef.current.style.backgroundImage = config.backgroundImage ? `url(${config.backgroundImage})` : 'none';
			this.badgeContainerRef.current.style.backgroundSize = 'cover';
			this.badgeContainerRef.current.style.backgroundPosition = 'center';
		}

		if (this.backgroundImgRef.current) {
			this.backgroundImgRef.current.src = config.backgroundImage || '';
			this.backgroundImgRef.current.classList.toggle('print-include', config.includeBackground);
		}

		// Canvas dimensions: actual 88mm × 140mm at 96 PPI
		const canvasWidthPx = Math.round((88 / 25.4) * 96);   // 333px - actual 88mm at 96 PPI (rounded to whole pixels)
		const canvasHeightPx = Math.round((140 / 25.4) * 96); // 529px - actual 140mm at 96 PPI (rounded to whole pixels)

		// Scale factors: how many display pixels per real badge pixel
		const scaleX = canvasWidthPx / config.badgeWidth;
		const scaleY = canvasHeightPx / config.badgeHeight;

		// Update dynamic field positions
		config.fieldsConfig.forEach((field, index) => {
			const element = this.fieldRefs[`field-${index}`];
			if (element) {
				element.style.left = (field.x * scaleX) + 'px';
				element.style.top = (field.y * scaleY) + 'px';
				element.style.fontSize = field.fontSize + 'px';
				element.style.fontFamily = field.fontFamily || 'Inter, sans-serif';
			}
		});

		// Update QR code position
		if (this.qrElementRef.current) {
			this.qrElementRef.current.style.left = (config.qrCode.x * scaleX) + 'px';
			this.qrElementRef.current.style.top = (config.qrCode.y * scaleY) + 'px';
			this.qrElementRef.current.style.width = (config.qrCode.size * scaleX) + 'px';
			this.qrElementRef.current.style.height = (config.qrCode.size * scaleY) + 'px';
			this.qrElementRef.current.style.fontFamily = 'Inter, sans-serif';
		}
	};

	handleMouseDown = (e) => {
		const target = e.target;

		if (target.classList.contains('resize-handle')) {
			this.setState({
				isResizing: true,
				resizeHandle: target,
				draggedElement: target.parentElement,
				originalRect: target.parentElement.getBoundingClientRect(),
				originalConfig: JSON.parse(JSON.stringify(this.state.config))
			});
			target.parentElement.style.zIndex = 1000;
		} else if (target.id.startsWith('field-') || target.id === 'qrElement' || (target.parentElement && (target.parentElement.id.startsWith('field-') || target.parentElement.id === 'qrElement'))) {
			const element = (target.id.startsWith('field-') || target.id === 'qrElement') ? target : target.parentElement;
			const rect = element.getBoundingClientRect();
			this.setState({
				draggedElement: element,
				offsetX: e.clientX - rect.left,
				offsetY: e.clientY - rect.top
			});
			element.style.zIndex = 1000;
		}
	};

	handleMouseMove = (e) => {
		const { isResizing, resizeHandle, draggedElement, originalRect, originalConfig, config } = this.state;

		if (isResizing && draggedElement && originalRect && originalConfig) {
			const containerRect = this.badgeContainerRef.current.getBoundingClientRect();
			const containerWidth = containerRect.width;
			const containerHeight = containerRect.height;

			const mouseX = e.clientX - containerRect.left;
			const mouseY = e.clientY - containerRect.top;

			let newLeft = originalRect.left - containerRect.left;
			let newTop = originalRect.top - containerRect.top;
			let newWidth = originalRect.width;
			let newHeight = originalRect.height;

			if (resizeHandle.classList.contains('se')) {
				newWidth = mouseX - newLeft;
				newHeight = mouseY - newTop;
			} else if (resizeHandle.classList.contains('sw')) {
				newWidth = (originalRect.right - containerRect.left) - mouseX;
				newHeight = mouseY - newTop;
				newLeft = mouseX;
			} else if (resizeHandle.classList.contains('ne')) {
				newWidth = mouseX - newLeft;
				newHeight = (originalRect.bottom - containerRect.top) - mouseY;
				newTop = mouseY;
			} else if (resizeHandle.classList.contains('nw')) {
				newWidth = (originalRect.right - containerRect.left) - mouseX;
				newHeight = (originalRect.bottom - containerRect.top) - mouseY;
				newLeft = mouseX;
				newTop = mouseY;
			}

			// Clamp dimensions to container boundaries
			newWidth = this.clamp(newWidth, 50, containerWidth);
			newHeight = this.clamp(newHeight, 30, containerHeight);
			newLeft = this.clamp(newLeft, 0, containerWidth - newWidth);
			newTop = this.clamp(newTop, 0, containerHeight - newHeight);

			// Update element style
			draggedElement.style.left = newLeft + 'px';
			draggedElement.style.top = newTop + 'px';
			if (draggedElement.id === 'qrElement') {
				// Keep QR code as a square - use the larger dimension for both width and height
				const squareSize = Math.max(newWidth, newHeight);
				draggedElement.style.width = squareSize + 'px';
				draggedElement.style.height = squareSize + 'px';
				newWidth = squareSize;
				newHeight = squareSize;
			}

			// Calculate dynamic scaling for precise coordinate mapping
			const scaleX = containerWidth / config.badgeWidth;
			const scaleY = containerHeight / config.badgeHeight;

			// Update config with precise real coordinates (maintain float precision)
			const realLeft = newLeft / scaleX;
			const realTop = newTop / scaleY;
			const realWidth = newWidth / scaleX;
			const realHeight = newHeight / scaleY;

			let newConfig = { ...config };

			if (draggedElement.id === 'qrElement') {
				newConfig.qrCode.x = Math.round(this.clamp(realLeft, 0, config.badgeWidth - realWidth));
				newConfig.qrCode.y = Math.round(this.clamp(realTop, 0, config.badgeHeight - realHeight));
				newConfig.qrCode.size = Math.round(Math.max(realWidth, realHeight));
				newConfig.qrCode.size = this.clamp(newConfig.qrCode.size, 5, 50);
			} else if (draggedElement.id.startsWith('field-')) {
				const fieldIndex = parseInt(draggedElement.id.split('-')[1]);
				if (newConfig.fieldsConfig[fieldIndex]) {
					newConfig.fieldsConfig[fieldIndex].x = Math.round(this.clamp(realLeft, 0, config.badgeWidth - 5));
					newConfig.fieldsConfig[fieldIndex].y = Math.round(this.clamp(realTop, 0, config.badgeHeight - 5));
					newConfig.fieldsConfig[fieldIndex].fontSize = Math.round(originalConfig.fieldsConfig[fieldIndex].fontSize * (newHeight / originalRect.height));
					newConfig.fieldsConfig[fieldIndex].fontSize = this.clamp(newConfig.fieldsConfig[fieldIndex].fontSize, 10, 500);
				}
			}

			this.setState({ config: newConfig, dragUpdate: this.state.dragUpdate + 1, editing: true });
		} else if (draggedElement) {
			const containerRect = this.badgeContainerRef.current.getBoundingClientRect();
			const containerWidth = containerRect.width;
			const containerHeight = containerRect.height;

			let newLeft = e.clientX - containerRect.left - this.state.offsetX;
			let newTop = e.clientY - containerRect.top - this.state.offsetY;

			// Clamp to container boundaries
			newLeft = this.clamp(newLeft, 0, containerWidth - draggedElement.offsetWidth);
			newTop = this.clamp(newTop, 0, containerHeight - draggedElement.offsetHeight);

			draggedElement.style.left = newLeft + 'px';
			draggedElement.style.top = newTop + 'px';

			// Calculate dynamic scaling for precise coordinate mapping
			const scaleX = containerWidth / config.badgeWidth;
			const scaleY = containerHeight / config.badgeHeight;

			// Update config with precise real coordinates (maintain float precision)
			const realX = newLeft / scaleX;
			const realY = newTop / scaleY;

			let newConfig = { ...config };

			if (draggedElement.id === 'qrElement') {
				newConfig.qrCode.x = Math.round(this.clamp(realX, 0, config.badgeWidth - config.qrCode.size));
				newConfig.qrCode.y = Math.round(this.clamp(realY, 0, config.badgeHeight - config.qrCode.size));
			} else if (draggedElement.id.startsWith('field-')) {
				const fieldIndex = parseInt(draggedElement.id.split('-')[1]);
				if (newConfig.fieldsConfig[fieldIndex]) {
					newConfig.fieldsConfig[fieldIndex].x = Math.round(this.clamp(realX, 0, config.badgeWidth - 5));
					newConfig.fieldsConfig[fieldIndex].y = Math.round(this.clamp(realY, 0, config.badgeHeight - 5));
				}
			}

			this.setState({ config: newConfig, dragUpdate: this.state.dragUpdate + 1, editing: true });
		}
	};

	handleMouseUp = () => {
		const { draggedElement } = this.state;
		if (draggedElement) {
			draggedElement.style.zIndex = '';
			this.setState({
				draggedElement: null,
				isResizing: false,
				resizeHandle: null,
				originalRect: null,
				originalConfig: null
			});
		}
	};

	handleInputChange = (field, value) => {
		const { config } = this.state;
		let newConfig = { ...config };

		if (field.startsWith('field-')) {
			const parts = field.split('-');
			const index = parseInt(parts[1]);
			const prop = parts[2];
			if (newConfig.fieldsConfig[index]) {
				if (prop === 'x' || prop === 'y') {
					newConfig.fieldsConfig[index][prop] = Math.round(parseFloat(value) || 0);
				} else if (prop === 'fontSize') {
					newConfig.fieldsConfig[index][prop] = parseInt(value) || 14;
				} else if (prop === 'fontFamily') {
					newConfig.fieldsConfig[index][prop] = value || 'Inter';
				}
			}
		} else if (field === 'qrSize') {
			newConfig.qrCode.size = Math.round(parseFloat(value) || 50);
		} else if (field === 'qrX') {
			newConfig.qrCode.x = Math.round(parseFloat(value) || 0);
		} else if (field === 'qrY') {
			newConfig.qrCode.y = Math.round(parseFloat(value) || 0);
		} else if (field === 'backgroundImage') {
			newConfig.backgroundImage = value;
		} else if (field === 'includeBackground') {
			newConfig.includeBackground = value;
		} else if (field === 'enableQRCode') {
			newConfig.enableQRCode = value;
		}

		this.setState({ config: newConfig, editing: true }, this.updateDisplay);
	};

	handleSave = async () => {
		await this.props.handleChange({ badgeConfiguration: this.state.config });
		await this.props.saveCheckInAppConfiguration();
		this.setState({ editing: false, baseConfiguration: JSON.parse(JSON.stringify(this.state.config)) });
	};

	cancelEdit = () => {
		this.setState({
			config: JSON.parse(JSON.stringify(this.state.baseConfiguration)),
			editing: false
		}, this.updateDisplay);
	};

	focusField = (fieldIndex) => {
		// Clear any drag state to prevent conflicts
		if (this.state.draggedElement || this.state.isResizing) return;

		this.setState({
			selectedPartialIndex: fieldIndex,
			componentSelected: true,
			focusedComponent: `#field-${fieldIndex}`,
			componentDetailsOpen: true,
			libraryPanelOpen: false,
			stylePanelOpen: false,
			settingsPanelOpen: false,
			selectedTab: 'editor' // Keep in editor tab when viewing field details
		});
	};
	unfocusField = () => {
		this.setState({
			selectedPartialIndex: null,
			componentSelected: false,
			focusedComponent: '',
			selectedTab: 'editor' // Return to field list view
		});
	};

	addFieldToBadge = (fieldValue) => {
		const { config, baseFields } = this.state;
		const fieldOption = baseFields.find(f => f.value === fieldValue.value);
		const newField = {
			field: fieldValue.value,
			label: fieldOption ? fieldOption.label : fieldValue.label,
			x: 10,
			y: 10,
			fontSize: 14,
			fontFamily: 'Inter'
		};
		const newConfig = { ...config };
		newConfig.fieldsConfig = [...newConfig.fieldsConfig, newField];
		this.setState({ config: newConfig, selectedBaseField: null, editing: true }, this.updateDisplay);
	};

	removeField = (index) => {
		const { config } = this.state;
		const newConfig = { ...config };
		newConfig.fieldsConfig.splice(index, 1);
		this.setState({ config: newConfig, editing: true }, this.updateDisplay);
	};



	handlePrint = () => {
		const { config } = this.state;

		// Use same canvas dimensions for identical appearance and precise positioning
		const canvasWidthPx = Math.round((88 / 25.4) * 96);   // Same as updateDisplay - actual 88mm at 96 PPI (rounded to whole pixels)
		const canvasHeightPx = Math.round((140 / 25.4) * 96); // Same as updateDisplay - actual 140mm at 96 PPI (rounded to whole pixels)

		// Calculate exact dimensions in inches for final print scaling
		const widthMm = 88;
		const heightMm = 140;
		const widthInches = widthMm / 25.4;
		const heightInches = heightMm / 25.4;

		// Create a new window for printing
		const printWindow = window.open('', 'PRINT', `width=${Math.round(canvasWidthPx)}px,height=${Math.round(canvasHeightPx)}px`);

		// Clone the badge container
		const badgeClone = this.badgeContainerRef.current.cloneNode(true);

		// Set badge dimensions to match canvas exactly (in pixels)
		badgeClone.style.width = canvasWidthPx + 'px';
		badgeClone.style.height = canvasHeightPx + 'px';
		badgeClone.style.border = 'none';
		badgeClone.style.position = 'relative';

		// Handle background image for print
		const backgroundImg = badgeClone.querySelector('img');
		if (backgroundImg) {
			// Reset all styles to match canvas exactly
			backgroundImg.style.cssText = '';
			backgroundImg.style.position = 'absolute';
			backgroundImg.style.top = '0';
			backgroundImg.style.left = '0';
			backgroundImg.style.width = '100%';
			backgroundImg.style.height = '100%';
			backgroundImg.style.objectFit = 'cover';
			backgroundImg.style.zIndex = '-1';
			backgroundImg.style.display = 'block';

			if (config.includeBackground) {
				backgroundImg.style.visibility = 'visible';
			} else {
				backgroundImg.style.display = 'none';
			}
		}

		// Use same scaling as canvas for identical positioning
		const scaleX = canvasWidthPx / config.badgeWidth; // Same as updateDisplay
		const scaleY = canvasHeightPx / config.badgeHeight; // Same as updateDisplay

		// Update element positions and sizes in pixels (same as canvas)
		config.fieldsConfig.forEach((field, index) => {
			const element = badgeClone.querySelector(`#field-${index}`);
			if (element) {
				element.style.cssText = '';
				element.className = '';
				element.style.position = 'absolute';
				element.style.margin = '0';
				element.style.padding = '0';
				element.style.left = (field.x * scaleX) + 'px';
				element.style.top = (field.y * scaleY) + 'px';
				element.style.fontSize = field.fontSize + 'px';
				element.style.fontFamily = field.fontFamily || 'Inter, sans-serif';
				element.style.color = '#000';

				// Remove resize handles
				const handles = element.querySelectorAll('.resize-handle');
				handles.forEach(handle => handle.remove());
			}
		});

		// Update QR code position
		const qrElement = badgeClone.querySelector('#qrElement');
		if (qrElement) {
			qrElement.style.cssText = '';
			qrElement.className = '';
			qrElement.style.position = 'absolute';
			qrElement.style.margin = '0';
			qrElement.style.padding = '0';
			qrElement.style.left = (config.qrCode.x * scaleX) + 'px';
			qrElement.style.top = (config.qrCode.y * scaleY) + 'px';
			qrElement.style.width = (config.qrCode.size * scaleX) + 'px';
			qrElement.style.height = (config.qrCode.size * scaleY) + 'px';
			qrElement.style.fontFamily = 'Inter, sans-serif';
			qrElement.style.backgroundColor = '#ccc';
			qrElement.style.border = '1px solid #999';
			qrElement.style.display = 'flex';
			qrElement.style.alignItems = 'center';
			qrElement.style.justifyContent = 'center';
			qrElement.style.fontSize = '12px';
			qrElement.style.color = '#666';

			// Remove resize handles
			const handles = qrElement.querySelectorAll('.resize-handle');
			handles.forEach(handle => handle.remove());
		}

		// Create print-specific HTML
		printWindow.document.write(`
			<!DOCTYPE html>
			<html>
			<head>
				<title>Badge Print</title>
				<style>
					* {
						margin: 0;
						padding: 0;
						box-sizing: border-box;
					}

					@font-face {
						font-family: 'Inter-Bold';
						src: url('https://system-fonts.s3.amazonaws.com/Inter/Inter-Bold.ttf') format('truetype');
						font-weight: bold;
						font-style: normal;
					}

					body, html {
						margin: 0;
						padding: 0;
						overflow: hidden;
					}

					.badge-container {
						width: ${Math.round(canvasWidthPx)}px;
						height: ${Math.round(canvasHeightPx)}px;
						page-break-inside: avoid !important;
						page-break-before: avoid !important;
						page-break-after: avoid !important;
					}

					@media print {
						@page {
							size: ${widthInches}in ${heightInches}in;
							margin: 0;
						}

						html, body {
							margin: 0 !important;
							padding: 0 !important;
							-webkit-print-color-adjust: exact !important;
							print-color-adjust: exact !important;
						}

						* {
							margin: 0 !important;
							padding: 0 !important;
							box-sizing: border-box !important;
						}

						img {
							display: block !important;
						}

						.badge-container {
							width: ${widthInches}in !important;
							height: ${heightInches}in !important;
							page-break-inside: avoid !important;
							page-break-before: avoid !important;
							page-break-after: avoid !important;
						}
					}
				</style>
			</head>
			<body>
		`);
		printWindow.document.write(badgeClone.outerHTML);
		printWindow.document.write(`
			</body>
			</html>
		`);

		printWindow.document.close();
		printWindow.focus();

		// Wait for content to load then print
		printWindow.onload = () => {
			// Short delay to ensure images are loaded
			setTimeout(() => {
				printWindow.print();
				setTimeout(() => {
					printWindow.close();
				}, 100);
			}, 500);
		};
	};

	toggleSection = (section) => {
		switch (section) {
			case 'editor':
				this.setState({
					selectedTab: 'editor',
					componentDetailsOpen: !this.state.componentDetailsOpen,
					libraryPanelOpen: false,
					stylePanelOpen: false,
					settingsPanelOpen: false
				});
				break;
			case 'settings':
				this.setState({
					selectedTab: 'settings',
					componentDetailsOpen: false,
					libraryPanelOpen: false,
					stylePanelOpen: false,
					settingsPanelOpen: !this.state.settingsPanelOpen
				});
				break;
			case 'library':
				this.setState({
					selectedTab: 'library',
					componentDetailsOpen: false,
					libraryPanelOpen: !this.state.libraryPanelOpen,
					stylePanelOpen: false,
					settingsPanelOpen: false
				});
				break;
			case 'style':
				this.setState({
					selectedTab: 'style',
					componentDetailsOpen: false,
					libraryPanelOpen: false,
					stylePanelOpen: !this.state.stylePanelOpen
				});
				break;
			default:
				break;
		}
	};

	onDragEnd = (result) => {
		// Handle drag-and-drop for library items
		if (!result.destination) {
			return;
		}

		const { source, destination } = result;

		// If dragging from library to badge preview
		if (source.droppableId === 'droppable-components' && destination.droppableId === 'droppable-page') {
			const draggedField = this.state.baseFields[source.index];
			this.addFieldToBadge(draggedField);
			// Automatically open the Editor tab when a field is added
			this.setState({
				selectedTab: 'editor',
				componentDetailsOpen: true,
				libraryPanelOpen: false,
				stylePanelOpen: false
			}, () => {
				// Get the index of the newly added field (it will be the last one)
				const newFieldIndex = this.state.config.fieldsConfig.length - 1;
				// Automatically focus on the newly added field to show editable settings
				this.focusField(newFieldIndex);
			});
		}
	};

	render() {
		const { isOpen, toggle, eventId, orgId, links } = this.props;
		const { config, editing, baseFields, componentDetailsOpen, libraryPanelOpen, settingsPanelOpen } = this.state;

		return (
			<Modal isOpen={isOpen} toggle={toggle} className="fullscreen">
				<div className='modalHeader ph-20' style={{ borderBottom: '1px solid var(--background)' }}>
					<div className='headerContainer' style={{ maxWidth: 'unset', minHeight: 60, height: 60 }}>
						<div className='flex aic'>
							<div>
								<h2>Badge Builder</h2>
								<div className='flex aic '>
									<h4>Badge Editor</h4>
								</div>
							</div>
						</div>
						<div className='flex aic'>
							<button className="neu mr-25 " onClick={() => {
								if (editing) {
									if (window.confirm('Are you sure you want to discard your changes?')) {
										this.cancelEdit();
										this.props.toggle();
									}
								} else {
									this.props.toggle();
								}
							}
							}>{editing ? 'Close & Discard Changes' : 'Close'}</button>
							<button className={`neu  ${editing ? 'prim' : 'disabled'}`}
								onClick={() => {
									this.handleSave();
								}}>Save</button>
						</div>
					</div>
				</div>
				<ModalBody className="modalBody">
					<DragDropContext onDragEnd={this.onDragEnd}>
						<div className='flex jcsb w-100 h-100'>
							<div className='designerPreview'>
								<div className='pagePreviewContainer2' style={{
									width: `${Math.round((88 / 25.4) * 96)}px`,  // 333px - actual 88mm at 96 PPI (rounded to whole pixels)

								}}>
									<style>
										{`
											.badge-element:hover .resize-handle {
												opacity: 1 !important;
											}
											.resize-handle:hover {
												background-color: #000 !important;
												border-color: #fff !important;
											}
											.field-selected {
												outline: 1px solid var(--primary) !important;

											}
										`}
									</style>
									<Droppable droppableId="droppable-page">
										{(provided, snapshot) => (
											<div
												{...provided.droppableProps}
												ref={provided.innerRef}
												style={{
													background: 'transparent',
													borderRadius: '4px',
													display: 'flex',
													justifyContent: 'center',
													alignItems: 'center',
													boxSizing: 'border-box',
												}}
											>
												<div
													className="badge-container"
													ref={this.badgeContainerRef}
													onMouseDown={this.handleMouseDown}
													onClick={(e) => {
														if (e.target === e.currentTarget) {
															this.unfocusField();
														}
													}}
													style={{
														width: `${Math.round((88 / 25.4) * 96)}px`,  // 333px - actual 88mm at 96 PPI (rounded to whole pixels)
														height: `${Math.round((140 / 25.4) * 96)}px`, // 529px - actual 140mm at 96 PPI (rounded to whole pixels)
														position: 'relative',
														backgroundColor: '#f9f9f9'
													}}
												>
													<img
														ref={this.backgroundImgRef}
														style={{
															position: 'absolute',
															top: 0,
															left: 0,
															width: '100%',
															height: '100%',
															objectFit: 'cover',
															zIndex: -1,
															display: 'block',
															visibility: 'hidden'
														}}
													/>
													{config.fieldsConfig && config.fieldsConfig.map((field, index) => (
														<div
															key={index}
															ref={(el) => this.fieldRefs[`field-${index}`] = el}
															id={`field-${index}`}
															className={`badge-element ${this.state.selectedPartialIndex === index ? 'field-selected' : ''}`}
															onClick={(e) => {
																e.stopPropagation();
																if (!this.state.draggedElement && !this.state.isResizing) {
																	this.focusField(index);
																}
															}}
															style={{
																position: 'absolute',
																cursor: 'move',
																color: '#000'
															}}
														>
															{field.label}<br />
														</div>
													))}
													{config.enableQRCode && (
														<div
															ref={this.qrElementRef}
															id="qrElement"
															onClick={(e) => {
																e.stopPropagation();
																if (!this.state.draggedElement && !this.state.isResizing) {
																	this.setState({
																		selectedPartialIndex: null,
																		componentDetailsOpen: false,
																		libraryPanelOpen: false,
																		stylePanelOpen: false,
																		settingsPanelOpen: true,
																		selectedTab: 'settings'
																	});
																}
															}}
															style={{
																position: 'absolute',
																cursor: 'move',
																backgroundColor: '#ccc',
																border: '1px solid #999',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																fontSize: '12px',
																color: '#666'
															}}
														>
															QR Code<br />

														</div>
													)}
												</div>
											</div>
										)}
									</Droppable>
								</div>
							</div>
							{/* Library Panel */}
							<div style={{ overflow: 'hidden', position: 'relative' }} className={`designerPanel p-0 right ${this.state.libraryPanelOpen ? '' : 'closed'}`}>
								<div style={{ padding: 25, paddingBottom: 60 }} className={`library formInputsSection selected`}>
									<button onClick={() => this.toggleSection('library')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>
									<h1 className='mb-15'>Library</h1>



									<Droppable droppableId="droppable-components">
										{(provided, snapshot) => (<div
											ref={provided.innerRef} className="fieldListContainer">
											{baseFields && baseFields.map((field, index) => {
												return true && (<Draggable
													key={`${field.value}_${index}`}
													draggableId={`${field.value}_${index}`}
													index={index}
												>
													{(provided, snapshot) => (
														<div
															ref={provided.innerRef}
															{...provided.draggableProps}
															{...provided.dragHandleProps} className={`fieldDraggable  `}
															onClick={() => this.addFieldToBadge(field)}>
															<i className="las la-braille mr-5" />

															<div>
																<h4>{field.label}</h4>
															</div>
														</div>)}
												</Draggable>)
											})}
										</div>)}
									</Droppable>

								</div>
							</div>

							{/* Style Panel */}
							<div style={{ overflow: 'hidden', position: 'relative' }} className={`designerPanel p-0 right ${this.state.stylePanelOpen ? '' : 'closed'}`}>
								<div style={{ padding: 25, paddingBottom: 60 }} className={`formInputsSection selected`}>
									<button onClick={() => this.toggleSection('style')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>
									<h1 className='mb-15'>Style</h1>
									<p className='mb-20'>Customize the visual appearance of your badge.</p>

									<div style={{ marginBottom: '15px' }}>
										<label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Badge Background</label>
										<div style={{ marginBottom: '10px' }}>
											<FileChooserPreview
												label="Background Image"
												value={config.backgroundImage}
												onClick={() => {
													this.setState({ uploadingBackground: true });
												}}
												clearFile={() => {
													this.handleInputChange('backgroundImage', '');
												}}
											/>
										</div>
										<div style={{ marginBottom: '10px' }}>
											<label style={{ display: 'block', marginBottom: '5px' }}>
												<input
													type="checkbox"
													checked={config.includeBackground}
													onChange={(e) => this.handleInputChange('includeBackground', e.target.checked)}
													style={{ marginRight: '5px' }}
												/>
												Include Background in Print
											</label>
										</div>
									</div>

									<div style={{ marginBottom: '15px' }}>
										<label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>QR Code Styling</label>
										<div style={{ marginBottom: '10px' }}>
											<ToggleField
												label="Enable QR Code on Badge"
												checked={config.enableQRCode}
												icons={false}
												onChange={(e) => {
													this.handleInputChange('enableQRCode', e.target.checked);
												}}
											/>
										</div>
										{config.enableQRCode && (
											<div style={{ paddingLeft: '20px' }}>
												<div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
													<div style={{ flex: 1 }}>
														<InputField
															label="QR X Position"
															type="number"
															value={config.qrCode.x}
															onChange={(e) => this.handleInputChange('qrX', e.target.value)}
														/>
													</div>
													<div style={{ flex: 1 }}>
														<InputField
															label="QR Y Position"
															type="number"
															value={config.qrCode.y}
															onChange={(e) => this.handleInputChange('qrY', e.target.value)}
														/>
													</div>
												</div>
												<div style={{ marginBottom: '10px' }}>
													<InputField
														label="QR Size"
														type="number"
														value={config.qrCode.size}
														onChange={(e) => this.handleInputChange('qrSize', e.target.value)}
													/>
												</div>
											</div>
										)}
									</div>

									<div style={{ marginBottom: '15px' }}>
										<label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Field Styling</label>
										{config.fieldsConfig.length > 0 ? (
											<div>
												{config.fieldsConfig.map((field, index) => (
													<div key={index} style={{ marginBottom: '15px', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
														<div style={{ marginBottom: '5px', fontWeight: 'bold' }}>{field.label}</div>
														<div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
															<div style={{ flex: 1 }}>
																<InputField
																	label="Font Size"
																	type="number"
																	value={field.fontSize}
																	onChange={(e) => this.handleInputChange(`field-${index}-fontSize`, e.target.value)}
																/>
															</div>
															<div style={{ flex: 1 }}>
																<InputField
																	label="Font Family"
																	value={field.fontFamily}
																	onChange={(e) => this.handleInputChange(`field-${index}-fontFamily`, e.target.value)}
																/>
															</div>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className='explainerBox' style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
												<p>Add fields to your badge first to customize their styling.</p>
											</div>
										)}
									</div>
								</div>
							</div>

							{/* Editor Panel - Consolidated with Field Details and Badge Settings */}
							<div style={{ overflow: 'hidden', position: 'relative' }} className={`designerPanel p-0 right ${this.state.componentDetailsOpen ? '' : 'closed'} ${this.state.componentDetailsExpanded ? 'expanded' : ''}`}>
								<div style={{ padding: 25, paddingBottom: 50 }} className={`formInputsSection selected`}>
									<button onClick={() => this.toggleSection('editor')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>

									{/* VIEW 1: Field Details (when a field is selected) */}
									{this.state.selectedPartialIndex !== null && config.fieldsConfig[this.state.selectedPartialIndex] ? (
										<div className='h-100 flex column'>
											<div className="flex-ac mb-10">
												<div onClick={this.unfocusField} className="c-pointer">
													<i className="las la-angle-left mr-5" />
													Back
												</div>
											</div>
											<h1 className='mb-15'>{config.fieldsConfig[this.state.selectedPartialIndex].label}</h1>

											<div style={{ display: 'flex', gap: '10px' }}>
												<div style={{ flex: 1 }}>
													<InputField
														label="X Position"
														type="number"
														value={config.fieldsConfig[this.state.selectedPartialIndex].x}
														onChange={(e) => this.handleInputChange(`field-${this.state.selectedPartialIndex}-x`, e.target.value)}
													/>
												</div>
												<div style={{ flex: 1 }}>
													<InputField
														label="Y Position"
														type="number"
														value={config.fieldsConfig[this.state.selectedPartialIndex].y}
														onChange={(e) => this.handleInputChange(`field-${this.state.selectedPartialIndex}-y`, e.target.value)}
													/>
												</div>
											</div>
											<InputField
												label="Font Size"
												type="number"
												value={config.fieldsConfig[this.state.selectedPartialIndex].fontSize}
												onChange={(e) => this.handleInputChange(`field-${this.state.selectedPartialIndex}-fontSize`, e.target.value)}
											/>
											<InputField
												label="Font Family"
												value={config.fieldsConfig[this.state.selectedPartialIndex].fontFamily}
												onChange={(e) => this.handleInputChange(`field-${this.state.selectedPartialIndex}-fontFamily`, e.target.value)}
											/>
											<button className='mt-a neu w-100 mb-20 danger'
												onClick={() => {
													this.removeField(this.state.selectedPartialIndex);
													this.unfocusField();
												}}>Remove Field</button>
										</div>
									) : (
										/* VIEW 2: Field List (default) */
										<div className='h-100 pb-20'>
											<h1 className='mb-10'>Editor</h1>
											<p className='mb-20'>Manage and edit fields on your badge.</p>

											{config.fieldsConfig.length > 0 ? (
												<div className=" h-100">
													{config.fieldsConfig.map((field, index) => (
														<div
															key={index}
															className={`fieldListItem ${this.state.selectedPartialIndex === index ? 'focused' : ''}`}
															style={{
																border: '2px solid var(--background)',
																borderRadius: 'var(--button-border-radius)',
																marginBottom: '10px',
																backgroundColor: this.state.selectedPartialIndex === index ? 'var(--background)' : '#fff'
															}}
														>
															<div className="flex-ac w-100" style={{ height: 50, padding: '0 12px' }}>
																<div
																	className="w-100 h-100 flex-ac c-pointer"
																	onClick={() => this.focusField(index)}
																>
																	<h4 className="mb-0">{field.label}</h4>
																</div>
																<div className="flex-ac">
																	<UncontrolledDropdown inNavbar>
																		<DropdownToggle className="columnToggle flex-ac">
																			<i className="las la-ellipsis-h" />
																		</DropdownToggle>
																		<DropdownMenu right className="columnDropdown p-0">
																			<DropdownItem
																				className="dropdownItem c-pointer"
																				onClick={(e) => {
																					e.stopPropagation();
																					this.removeField(index);
																					if (this.state.selectedPartialIndex === index) {
																						this.unfocusField();
																					}
																				}}
																			>
																				<div className="red">
																					<i className="las la-trash mr-5" />Remove
																				</div>
																			</DropdownItem>
																		</DropdownMenu>
																	</UncontrolledDropdown>
																</div>
															</div>
														</div>
													))}
												</div>
											) : (
												<div className='explainerBox' style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', marginTop: '15px' }}>
													<p>No fields added yet. Drag fields from the Library to get started.</p>
												</div>
											)}
										</div>
									)}
								</div>
							</div>

							{/* Settings Panel */}
							<div style={{ overflow: 'hidden', position: 'relative' }} className={`designerPanel p-0 right ${this.state.settingsPanelOpen ? '' : 'closed'}`}>
								<div style={{ padding: 25, paddingBottom: 60 }} className={`formInputsSection selected`}>
									<button onClick={() => this.toggleSection('settings')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>
									<h1 className='mb-15'>Badge Settings</h1>

									<InputField
										label="Badge Width (px)"
										type="number"
										value={config.badgeWidth}
										disabled={true}
									/>
									<InputField
										label="Badge Height (px)"
										type="number"
										value={config.badgeHeight}
										disabled={true}
									/>
									<InputField
										label="Actual Size"
										type="text"
										value="88mm × 140mm (3.46in × 5.51in)"
										disabled={true}
									/>
									<FileChooserPreview
										label="Background Image"
										value={config.backgroundImage}
										onClick={() => {
											this.setState({ uploadingBackground: true });
										}}
										clearFile={() => {
											this.handleInputChange('backgroundImage', '');
										}}
									/>
									<ToggleField
										label="Include Background in Print"
										checked={config.includeBackground}
										icons={false}
										onChange={(e) => this.handleInputChange('includeBackground', e.target.checked)}
									/>

									<ToggleField
										label="Enable QR Code on Badge"
										checked={config.enableQRCode}
										icons={false}
										onChange={(e) => {
											this.handleInputChange('enableQRCode', e.target.checked);
										}}
									/>

									{config.enableQRCode && (
										<>
											<div style={{ display: 'flex', gap: '10px' }}>
												<div style={{ flex: 1 }}>
													<InputField
														label="QR X Position"
														type="number"
														value={config.qrCode.x}
														onChange={(e) => this.handleInputChange('qrX', e.target.value)}
													/>
												</div>
												<div style={{ flex: 1 }}>
													<InputField
														label="QR Y Position"
														type="number"
														value={config.qrCode.y}
														onChange={(e) => this.handleInputChange('qrY', e.target.value)}
													/>
												</div>
											</div>
											<InputField
												label="QR Size"
												type="number"
												value={config.qrCode.size}
												onChange={(e) => this.handleInputChange('qrSize', e.target.value)}
											/>
										</>
									)}

									{/* <div style={{ marginTop: '20px' }}>
										<button
											onClick={this.handlePrint}
											style={{
												width: '100%',
												padding: '10px',
												background: 'var(--primary)',
												color: '#fff',
												border: 'none',
												borderRadius: '4px',
												cursor: 'pointer'
											}}
										>
											<i className='las la-print mr-5'></i>
											Print Badge
										</button>
									</div> */}
								</div>
							</div>

							<div className='designerPanel side p-0' style={{ overflow: 'hidden', position: 'relative', minWidth: 100, width: 100 }} >
								<div className='flex column jcc'>
									<div className={`flex column jcc aic c-pointer ${componentDetailsOpen ? 'selectedSection' : ''} `} style={{ height: 100 }} onClick={() => {
										this.toggleSection('editor')
									}}>
										<i className='las la-edit mb-10' style={{ fontSize: 30 }}></i>
										<h5>Editor</h5></div>
									<div className={`flex column jcc aic c-pointer library ${libraryPanelOpen ? 'selectedSection' : ''} `} style={{ height: 100 }} onClick={() => {
										this.toggleSection('library')
									}}>
										<i className='las la-book mb-10' style={{ fontSize: 30 }}></i>
										<h5>Library</h5>
									</div>
									<div className={`flex column jcc aic c-pointer ${this.state.settingsPanelOpen ? 'selectedSection' : ''} `} style={{ height: 100 }} onClick={() => {
										this.toggleSection('settings')
									}}>
										<i className='las la-cog mb-10' style={{ fontSize: 30 }}></i>
										<h5>Settings</h5>
									</div>

								</div>
							</div>
						</div>
						{this.state.uploadingBackground && (
							<FilesUploaderModal
								isOpen={this.state.uploadingBackground}
								toggle={() => {
									this.setState({ uploadingBackground: false });
								}}
								eventId={eventId}
								links={links}
								orgId={orgId}
								addFiles={(fileURL) => {
									this.handleInputChange('backgroundImage', fileURL);
									this.setState({ uploadingBackground: false });
								}}
							/>
						)}
					</DragDropContext>
				</ModalBody>
			</Modal>
		);
	}
}

export default withRouter(BadgeEditor);
